import type { PlayerPortalGetCompetitionDetailsResponse } from "@mixitone/actions";
import { Panel } from "@mixitone/components";
import TrophyIcon from "@mixitone/icons/trophy.svg";
import { toOrdinal, toSentence } from "@mixitone/util";

type Winner = PlayerPortalGetCompetitionDetailsResponse["winners"][number];

interface CompetitionResultsPanelProps {
  competitionName: string;
  winners: Winner[];
}

function groupWinnersByDivision(winners: Winner[]): Array<[number, Winner[]]> | null {
  const hasDivisions = winners.some((winner) => winner.division != null);
  if (!hasDivisions) return null;

  const byDivision = new Map<number, Winner[]>();
  for (const winner of winners) {
    if (winner.division == null) continue;
    const list = byDivision.get(winner.division) ?? [];
    list.push(winner);
    byDivision.set(winner.division, list);
  }

  return [...byDivision.entries()].sort((a, b) => a[0] - b[0]);
}

const WinnerLine = ({ winner }: { winner: Winner }) => (
  <div className={winner.isYou ? "rounded-md bg-amber-50 px-2 py-1 text-sm" : "text-sm"}>
    <span className="font-medium">{toOrdinal(winner.place)} place: </span>
    <span className={winner.isYou ? "font-semibold" : undefined}>{toSentence([...winner.names])}</span>
    {winner.isYou ? <span className="text-slate-500"> (you)</span> : null}
  </div>
);

export const CompetitionResultsPanel = ({ competitionName, winners }: CompetitionResultsPanelProps) => {
  if (winners.length === 0) return null;

  const grouped = groupWinnersByDivision(winners);

  return (
    <Panel title="Results" titleClassName="font-semibold">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <TrophyIcon className="flex-shrink-0 w-8 h-8 fill-yellow-400" />
          <p className="text-lg font-semibold text-slate-900">{competitionName} has finished</p>
        </div>
        <div className="p-3 rounded-md bg-slate-50">
          {grouped
            ? grouped.map(([division, divisionWinners]) => (
                <div key={division} className="mb-4 text-sm last:mb-0">
                  <h4 className="mb-2 text-sm font-medium text-blue-500">Division {division}</h4>
                  <div className="ml-2 space-y-1">
                    {divisionWinners.map((winner) => (
                      <WinnerLine
                        key={`${division}-${winner.place}-${winner.names.join("-")}`}
                        winner={winner}
                      />
                    ))}
                  </div>
                </div>
              ))
            : winners.map((winner) => (
                <div key={`${winner.place}-${winner.names.join("-")}`} className="mb-1 last:mb-0">
                  <WinnerLine winner={winner} />
                </div>
              ))}
        </div>
      </div>
    </Panel>
  );
};
